import getLang from 'I18n/utils/getLang';
import PortalIdParser from 'PortalIdParser';
import { getFullUrl } from 'hubspot-url-utils';
import { na1 } from 'hubspot-url-utils/hublets';
import { AppContextType } from 'marketplace-ui-common/types';
import { getPublicLocalizedPathname } from 'marketplace-ui-common/utils/urls';
import { deployed } from './env';

export function getRootUrl() {
  return `/marketplace/${PortalIdParser.get()}`;
}

export function getShortlinkUrl() {
  return '/l/marketplace';
}

function getBaseUrl(appContext: AppContextType) {
  return getFullUrl(appContext === 'app' ? 'app' : 'ecosystem', {
    hubletOverride: appContext === 'public' ? na1 : undefined,
  });
}

export function getFullMarketplaceUrl(
  appContext: AppContextType,
  path: string,
  lang?: string
): string {
  const hrefLang = lang || getLang();

  const baseUrl = getBaseUrl(appContext);
  if (appContext === 'app') {
    return `${baseUrl}/marketplace/${PortalIdParser.get()}${path.replace(
      /\/+$/,
      ''
    )}`;
  }

  return `${baseUrl}${getPublicRootUrl(hrefLang)}${path.replace(/\/+$/, '')}`;
}

export function getFullPublicUrl(path: string, lang?: string): string {
  return getFullMarketplaceUrl('public', path, lang);
}

export function getFullAppUrl(path: string, lang?: string): string {
  return getFullMarketplaceUrl('app', path, lang);
}

export function getPublicRootUrl(lang = 'en') {
  return `${getPublicLocalizedPathname(lang)}/marketplace`;
}

export const getTemplateMarketplaceUrl = (
  appContext: AppContextType,
  lang: string
) => {
  const baseUrl = getBaseUrl(appContext);
  if (appContext === 'app') {
    return `${baseUrl}/ecosystem/${PortalIdParser.get()}/marketplace/templates`;
  }

  return `${baseUrl}${getPublicRootUrl(lang)}/templates`;
};

export const getSolutionsDirectoryUrl = (
  appContext: AppContextType,
  lang: string
) => {
  const baseUrl = getBaseUrl(appContext);
  if (appContext === 'app') {
    return `${baseUrl}/ecosystem/${PortalIdParser.get()}/marketplace/solutions`;
  }

  return `${baseUrl}${getPublicRootUrl(lang)}/solutions`;
};

export const getConnectedAppUrl = (appContext: AppContextType) => {
  const baseUrl = getBaseUrl(appContext);
  return `${baseUrl}/connected-apps/${PortalIdParser.get()}`;
};

export const getAddedAgentsUrl = (appContext: AppContextType) => {
  const baseUrl = getBaseUrl(appContext);
  return `${baseUrl}/agent-hub/${PortalIdParser.get()}/manage-agents/agents`;
};

export const getMarketplaceDownloadsUrl = (appContext: AppContextType) => {
  const baseUrl = getBaseUrl(appContext);
  return `${baseUrl}/marketplace-settings/${PortalIdParser.get()}/downloads`;
};

export function getWindowLocation() {
  return window.location;
}

export function getListingDetailsShortlink(pathname: string, params = {}) {
  const queryString = Object.keys(params).length
    ? `?${new URLSearchParams(params).toString()}`
    : '';
  return `${getShortlinkUrl()}${pathname}${queryString}`;
}

// forces app url prefix to redirect to login
export function getListingDetailsLoginlink(pathname: string, params = {}) {
  const baseUrl = getBaseUrl('app');
  const queryString = Object.keys(params).length
    ? `?${new URLSearchParams(params).toString()}`
    : '';
  return `${baseUrl}${getShortlinkUrl()}${pathname}${queryString}`;
}

export function getAutomationOverviewUrl(): string | null {
  const portalId = PortalIdParser.get();
  if (!portalId) {
    return null;
  }
  return `${getBaseUrl('app')}/automation/${portalId}/overview`;
}

export const REFERRER_SPOOF_STORAGE_KEY = 'marketplace_debug_referrer';

export function getValidDocumentReferrer(): string | null {
  let spoofedReferrer: string | null = null;
  if (!deployed()) {
    try {
      spoofedReferrer = localStorage.getItem(REFERRER_SPOOF_STORAGE_KEY);
    } catch {
      // localStorage unavailable
    }
  }
  const referrer = spoofedReferrer || document.referrer;
  if (!referrer) return null;

  try {
    const referrerUrl = new URL(referrer);
    if (referrerUrl.origin === window.location.origin) {
      return referrer;
    }
  } catch {
    // Invalid URL
  }

  return null;
}

export function stripPortalId(url: string) {
  const portalId = PortalIdParser.get();
  if (!portalId) {
    return url;
  }
  const portalIdRegex = new RegExp(portalId.toString(), 'g');
  return url.replace(portalIdRegex, '<portal_id>');
}
